import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useToaster } from '@components/toaster';

import { useStore } from 'models/Provider';

import {
  completionTemplates,
  createCompletionTemplate,
  onboardingFormEmails,
  onboardingFormEnabled,
  onboardingFormRemoveEmail,
  removeCompletionTemplate,
  SubmitError,
  updateCompletionTemplate,
} from './requests';

export function useIsOnboardingFormEnabled() {
  const query = useQuery({
    queryKey: ['onboardingFormEnabled'],
    queryFn: onboardingFormEnabled,
  });

  return query.isSuccess && query.data.enabled;
}

export function useNotifyEmailsList() {
  const { showError } = useToaster();
  const query = useQuery({
    queryKey: ['notify-email-data'],
    queryFn: onboardingFormEmails,
    onError: (error: SubmitError) => {
      showError(error.response ? error.response.data.error : error.message);
    },
  });
  return query;
}

export function useRemoveNotifyRecipient() {
  const { t } = useStore();
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation((recipientId: number) => onboardingFormRemoveEmail(recipientId), {
    onSuccess: () => {
      showSuccess(t('integrations.onboardingform.settings.emails.remove.success'));
      return queryClient.invalidateQueries(['notify-email-data']);
    },
    onError: (error: SubmitError) => {
      showError(error.response ? error.response.data.error : error.message);
    },
  });
}

export const useGetCompletionTemplates = () => {
  const { showError } = useToaster();
  const { t } = useStore();
  return useQuery({
    queryKey: ['completion-templates'],
    queryFn: completionTemplates,
    onError: () => {
      showError(t('integrations.onboardingform.settings.email.get-templates.error'));
    },
  });
};

export const useCreateCompletionTemplate = (onCreateSuccess: () => void) => {
  const { t } = useStore();
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation(
    (data: { type: string; subject: string; content: string }) =>
      createCompletionTemplate(data.type, data.subject, data.content),
    {
      mutationKey: ['create-completion-template'],
      onSuccess: () => {
        showSuccess(t('integrations.onboardingform.settings.email.create.success'));
        onCreateSuccess();
        return queryClient.invalidateQueries(['completion-templates']);
      },
      onError: (error: SubmitError) => {
        showError(error.response ? error.response.data.error : error.message);
      },
    },
  );
};

export const useUpdateCompletionTemplate = (closeModal: () => void) => {
  const { t } = useStore();
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();
  const query = useMutation(
    (data: { templateId: number; subject: string; content: string }) =>
      updateCompletionTemplate(data.templateId, data.subject, data.content),
    {
      mutationKey: ['update-completion-template'],
      onSuccess: () => {
        showSuccess(t('integrations.onboardingform.settings.email.update.success'));
        closeModal();
        return queryClient.invalidateQueries(['completion-templates']);
      },
      onError: (error: SubmitError) => {
        showError(error.response ? error.response.data.error : error.message);
      },
    },
  );
  return query;
};

export const useDeleteCompletionTemplate = (onDisableSuccess: () => void) => {
  const { t } = useStore();
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation((templateId: number) => removeCompletionTemplate(templateId), {
    mutationKey: ['delete-completion-template'],
    onSuccess: () => {
      onDisableSuccess();
      showSuccess(t('integrations.onboardingform.settings.email.disable.success'));
      return queryClient.invalidateQueries(['completion-templates']);
    },
    onError: () => {
      showError(t('integrations.onboardingform.settings.email.disable.error'));
    },
  });
};
